import React, { useEffect, useState } from "react";
import { Provider, useDispatch, useSelector } from "react-redux";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigationType,
  useSearchParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../src/localization/i18n.ts";
import EmailVerificationResult from "./pages/auth/EmailVerificationResult";
import AppStoreScreenshots from "./pages/internal/AppStoreScreenshots";
import LandingCardRecorder from "./pages/internal/LandingCardRecorder";
import StyleGuide from "./pages/internal/StyleGuide";
import DesignPlayground from "./pages/internal/DesignPlayground";
import YouWoBusinessLanding from "./pages/internal/YouWoBusinessLanding";
import YouWoConsumerLanding from "./pages/internal/YouWoConsumerLanding";
import YouWoDemoBooking from "./pages/internal/YouWoDemoBooking";
import DownloadDesktop from "./pages/internal/DownloadDesktop";
import CompetitorAnalysis from "./pages/internal/CompetitorAnalysis";
import InternalDashboard from "./pages/internal/InternalDashboard";
import SupportAccess from "./pages/internal/SupportAccess";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import ResetPasswordEmail from "./pages/auth/ResetPasswordEmail";
import ResetPasswordSuccess from "./pages/auth/ResetPasswordSuccess";
import SignIn from "./pages/auth/SignIn";
import SignUp from "./pages/auth/SignUp";
import VerifyEmail from "./pages/auth/VerifyEmail";
import DataDeletion from "./pages/DataDeletion";
import LandingPage from "./pages/LandingPage";
import ModernLandingPage from "./pages/ModernLandingPage";
import ProductPage from "./pages/ProductPage";
import LearnflowPage from "./pages/LearnflowPage";
import InternationalMarketingPage from "./pages/InternationalMarketingPage";
import BusinessPage from "./pages/BusinessPage";
import BusinessBriefPage from "./pages/BusinessBriefPage";
import MissionPage from "./pages/MissionPage";
import StoryPage from "./pages/StoryPage";
import FounderPage from "./pages/FounderPage";
import { JournalPage, JournalArticlePage } from "./pages/blog/JournalPage";
import PricingPage from "./pages/PricingPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Support from "./pages/SupportPage";
import MemoryBenchmarks from "./pages/research/MemoryBenchmarks";
// Free AI Tools Pages
import {
  AllTools,
  YouTubeSummarizer,
  PDFSummarizer,
  AudioTranscriber,
  FlashcardMaker,
  QuizGenerator,
  MeetingNotes,
  LectureNotes,
  // SEO Landing Pages
  QuizletAlternative,
  AnkiAlternative,
  NotabilityAlternative,
  OtterAIAlternative,
  ChatGPTForStudents,
  AIStudyTools,
  AIFlashcardGenerator,
  LectureTranscription,
  PDFToFlashcards,
  AINoteTaking,
} from "./pages/tools";
import { injectStore } from "./redux/api";
import { fetchUser } from "./redux/features/user/userSlice";
import store, { AppDispatch, RootState } from "./redux/store";

const INTERNAL_ACCESS_KEY = "youwoai2026";
const INTERNAL_ACCESS_STORAGE_KEY = "youwo-internal-access-key";

// Protected route for internal tools.
const ProtectedInternalRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const key = searchParams.get("key");
  const isDev = process.env.NODE_ENV === "development";
  const [enteredKey, setEnteredKey] = useState("");
  const [storedKey, setStoredKey] = useState(() => {
    try {
      return sessionStorage.getItem(INTERNAL_ACCESS_STORAGE_KEY) || "";
    } catch {
      return "";
    }
  });
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    if (key === INTERNAL_ACCESS_KEY) {
      try {
        sessionStorage.setItem(INTERNAL_ACCESS_STORAGE_KEY, key);
      } catch {
        // Session storage can be unavailable in private/browser-restricted modes.
      }
      setStoredKey(key);
    }
  }, [key]);

  if (isDev || key === INTERNAL_ACCESS_KEY || storedKey === INTERNAL_ACCESS_KEY) {
    return <>{children}</>;
  }

  const unlock = (event: React.FormEvent) => {
    event.preventDefault();
    if (enteredKey === INTERNAL_ACCESS_KEY) {
      try {
        sessionStorage.setItem(INTERNAL_ACCESS_STORAGE_KEY, enteredKey);
      } catch {
        // Session storage can be unavailable in private/browser-restricted modes.
      }
      setStoredKey(enteredKey);
      setInvalid(false);
      return;
    }
    setInvalid(true);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "#f6f7f9",
        color: "#111827",
        fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
        padding: 24,
      }}
    >
      <form
        onSubmit={unlock}
        style={{
          width: "100%",
          maxWidth: 380,
          background: "#fff",
          border: "1px solid #e1e5ea",
          borderRadius: 8,
          padding: 20,
          display: "grid",
          gap: 12,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 18 }}>Internal Access</h1>
        <input
          value={enteredKey}
          onChange={(event) => {
            setEnteredKey(event.target.value);
            setInvalid(false);
          }}
          type="password"
          autoFocus
          placeholder="Access key"
          style={{
            border: "1px solid #d6d9df",
            borderRadius: 6,
            padding: "10px 12px",
            fontSize: 14,
          }}
        />
        {invalid && (
          <div style={{ color: "#991b1b", fontSize: 13 }}>
            Invalid access key for {location.pathname}
          </div>
        )}
        <button
          type="submit"
          style={{
            border: "1px solid #111827",
            borderRadius: 6,
            background: "#111827",
            color: "#fff",
            padding: "10px 14px",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Continue
        </button>
      </form>
    </div>
  );
};

function AppContent() {
  const action = useNavigationType();
  const location = useLocation();
  const pathname = location.pathname;
  const isLoggedIn = useSelector((state: RootState) => state.auth.isLoggedIn);
  const userRole = useSelector((state: RootState) => state.user.user?.role);
  const user = useSelector((state: RootState) => state.user.user);
  const currentLanguage = useSelector((state: RootState) => state.language.current);
  const dispatch: AppDispatch = useDispatch();
  const { i18n } = useTranslation();

  // Sync Redux language with i18n on mount
  useEffect(() => {
    if (currentLanguage && i18n.language !== currentLanguage) {
      i18n.changeLanguage(currentLanguage);
    }
  }, [currentLanguage, i18n]);

  useEffect(() => {
    if (action !== "POP" && !location.hash) {
      window.scrollTo(0, 0);
    }
  }, [action, pathname, location.hash]);

  useEffect(() => {
    console.log("isLoggedIn", isLoggedIn);
    if (isLoggedIn) {
      dispatch(fetchUser());
    }
  }, [isLoggedIn, dispatch, userRole]);

  return (
    <Routes>
      {!isLoggedIn ? (
        <>
          <Route path="/" element={<ModernLandingPage />} />
          <Route path="/product" element={<ProductPage />} />
          <Route path="/learnflow" element={<LearnflowPage />} />
          <Route path="/:locale" element={<InternationalMarketingPage section="home" />} />
          <Route path="/:locale/business" element={<InternationalMarketingPage section="business" />} />
          <Route path="/:locale/product" element={<InternationalMarketingPage section="product" />} />
          <Route path="/:locale/learnflow" element={<InternationalMarketingPage section="learnflow" />} />
          <Route path="/business" element={<BusinessPage />} />
          <Route path="/business/analysis" element={<BusinessBriefPage index={0} />} />
          <Route path="/business/growth" element={<BusinessBriefPage index={1} />} />
          <Route path="/business/workflows" element={<BusinessBriefPage index={2} />} />
          <Route path="/mission" element={<MissionPage />} />
          <Route path="/story" element={<StoryPage />} />
          <Route path="/founder" element={<FounderPage />} />
              <Route path="/blog" element={<JournalPage />} />
              <Route path="/blog/:slug" element={<JournalArticlePage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="/landing-page" element={<ModernLandingPage />} />
          <Route path="/old-landing" element={<LandingPage />} />
          <Route path="/modern-landing" element={<ModernLandingPage />} />
          <Route path="/support" element={<Support />} />
          <Route path="/research" element={<Navigate to="/research/memory-benchmarks" replace />} />
          <Route path="/research/memory-benchmarks" element={<MemoryBenchmarks />} />
          {/* Free AI Tools Routes */}
          <Route path="/tools" element={<AllTools />} />
          <Route path="/tools/youtube-summarizer" element={<YouTubeSummarizer />} />
          <Route path="/tools/pdf-summarizer" element={<PDFSummarizer />} />
          <Route path="/tools/audio-transcriber" element={<AudioTranscriber />} />
          <Route path="/tools/flashcard-maker" element={<FlashcardMaker />} />
          <Route path="/tools/quiz-generator" element={<QuizGenerator />} />
          <Route path="/tools/meeting-notes" element={<MeetingNotes />} />
          <Route path="/tools/lecture-notes" element={<LectureNotes />} />
          {/* SEO Landing Pages */}
          <Route path="/tools/quizlet-alternative" element={<QuizletAlternative />} />
          <Route path="/tools/anki-alternative" element={<AnkiAlternative />} />
          <Route path="/tools/notability-alternative" element={<NotabilityAlternative />} />
          <Route path="/tools/otter-ai-alternative" element={<OtterAIAlternative />} />
          <Route path="/tools/chatgpt-for-students" element={<ChatGPTForStudents />} />
          <Route path="/tools/ai-study-tools" element={<AIStudyTools />} />
          <Route path="/tools/ai-flashcard-generator" element={<AIFlashcardGenerator />} />
          <Route path="/tools/lecture-transcription" element={<LectureTranscription />} />
          <Route path="/tools/pdf-to-flashcards" element={<PDFToFlashcards />} />
          <Route path="/tools/ai-note-taking" element={<AINoteTaking />} />
          {/* Internal tools - protected by query param */}
          <Route path="/internal/screenshots" element={<ProtectedInternalRoute><AppStoreScreenshots /></ProtectedInternalRoute>} />
          <Route path="/internal/landing-cards" element={<ProtectedInternalRoute><LandingCardRecorder /></ProtectedInternalRoute>} />
          <Route path="/internal/style-guide" element={<ProtectedInternalRoute><StyleGuide /></ProtectedInternalRoute>} />
          <Route path="/internal/design-playground" element={<ProtectedInternalRoute><DesignPlayground /></ProtectedInternalRoute>} />
          <Route path="/internal/youwo-business-landing" element={<ProtectedInternalRoute><YouWoBusinessLanding /></ProtectedInternalRoute>} />
          <Route path="/internal/youwo-consumer-landing" element={<ProtectedInternalRoute><YouWoConsumerLanding /></ProtectedInternalRoute>} />
          <Route path="/internal/youwo-context-landing" element={<ProtectedInternalRoute><YouWoConsumerLanding variant="source" /></ProtectedInternalRoute>} />
          <Route path="/internal/youwo-demo-booking" element={<ProtectedInternalRoute><YouWoDemoBooking /></ProtectedInternalRoute>} />
          <Route path="/internal/download" element={<ProtectedInternalRoute><DownloadDesktop /></ProtectedInternalRoute>} />
          <Route path="/internal/competitors" element={<ProtectedInternalRoute><CompetitorAnalysis /></ProtectedInternalRoute>} />
          <Route path="/internal/dashboard" element={<ProtectedInternalRoute><InternalDashboard /></ProtectedInternalRoute>} />
          <Route path="/internal/admin-support" element={<ProtectedInternalRoute><SupportAccess /></ProtectedInternalRoute>} />
          <Route path="/internal/support-access" element={<ProtectedInternalRoute><SupportAccess /></ProtectedInternalRoute>} />
          {/* <Route path="/sign-in" element={<SignIn />} />
          <Route path="/sign-up" element={<SignUp />} /> */}
          {/* <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} /> */}
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/reset-password-success"
            element={<ResetPasswordSuccess />}
          />
          <Route
            path="/email-verification"
            element={<EmailVerificationResult />}
          />
          {/* <Route
            path="/reset-password-email"
            element={<ResetPasswordEmail />}
          /> */}
          <Route path="*" element={<Navigate to="landing-page" />} />
        </>
      ) : (
        // temp holder
        <>
          <Route path="/" element={<ModernLandingPage />} />
          <Route path="/product" element={<ProductPage />} />
          <Route path="/business" element={<BusinessPage />} />
          <Route path="/business/analysis" element={<BusinessBriefPage index={0} />} />
          <Route path="/business/growth" element={<BusinessBriefPage index={1} />} />
          <Route path="/business/workflows" element={<BusinessBriefPage index={2} />} />
          <Route path="/mission" element={<MissionPage />} />
          <Route path="/story" element={<StoryPage />} />
          <Route path="/founder" element={<FounderPage />} />
              <Route path="/blog" element={<JournalPage />} />
              <Route path="/blog/:slug" element={<JournalArticlePage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="/landing-page" element={<ModernLandingPage />} />
          <Route path="/old-landing" element={<LandingPage />} />
          <Route path="/modern-landing" element={<ModernLandingPage />} />
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/sign-up" element={<SignUp />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/support" element={<Support />} />
          <Route path="/research" element={<Navigate to="/research/memory-benchmarks" replace />} />
          <Route path="/research/memory-benchmarks" element={<MemoryBenchmarks />} />
          {/* Free AI Tools Routes */}
          <Route path="/tools" element={<AllTools />} />
          <Route path="/tools/youtube-summarizer" element={<YouTubeSummarizer />} />
          <Route path="/tools/pdf-summarizer" element={<PDFSummarizer />} />
          <Route path="/tools/audio-transcriber" element={<AudioTranscriber />} />
          <Route path="/tools/flashcard-maker" element={<FlashcardMaker />} />
          <Route path="/tools/quiz-generator" element={<QuizGenerator />} />
          <Route path="/tools/meeting-notes" element={<MeetingNotes />} />
          <Route path="/tools/lecture-notes" element={<LectureNotes />} />
          {/* SEO Landing Pages */}
          <Route path="/tools/quizlet-alternative" element={<QuizletAlternative />} />
          <Route path="/tools/anki-alternative" element={<AnkiAlternative />} />
          <Route path="/tools/notability-alternative" element={<NotabilityAlternative />} />
          <Route path="/tools/otter-ai-alternative" element={<OtterAIAlternative />} />
          <Route path="/tools/chatgpt-for-students" element={<ChatGPTForStudents />} />
          <Route path="/tools/ai-study-tools" element={<AIStudyTools />} />
          <Route path="/tools/ai-flashcard-generator" element={<AIFlashcardGenerator />} />
          <Route path="/tools/lecture-transcription" element={<LectureTranscription />} />
          <Route path="/tools/pdf-to-flashcards" element={<PDFToFlashcards />} />
          <Route path="/tools/ai-note-taking" element={<AINoteTaking />} />
          {/* Internal tools - protected by query param */}
          <Route path="/internal/screenshots" element={<ProtectedInternalRoute><AppStoreScreenshots /></ProtectedInternalRoute>} />
          <Route path="/internal/landing-cards" element={<ProtectedInternalRoute><LandingCardRecorder /></ProtectedInternalRoute>} />
          <Route path="/internal/style-guide" element={<ProtectedInternalRoute><StyleGuide /></ProtectedInternalRoute>} />
          <Route path="/internal/design-playground" element={<ProtectedInternalRoute><DesignPlayground /></ProtectedInternalRoute>} />
          <Route path="/internal/youwo-business-landing" element={<ProtectedInternalRoute><YouWoBusinessLanding /></ProtectedInternalRoute>} />
          <Route path="/internal/youwo-consumer-landing" element={<ProtectedInternalRoute><YouWoConsumerLanding /></ProtectedInternalRoute>} />
          <Route path="/internal/youwo-context-landing" element={<ProtectedInternalRoute><YouWoConsumerLanding variant="source" /></ProtectedInternalRoute>} />
          <Route path="/internal/youwo-demo-booking" element={<ProtectedInternalRoute><YouWoDemoBooking /></ProtectedInternalRoute>} />
          <Route path="/internal/download" element={<ProtectedInternalRoute><DownloadDesktop /></ProtectedInternalRoute>} />
          <Route path="/internal/competitors" element={<ProtectedInternalRoute><CompetitorAnalysis /></ProtectedInternalRoute>} />
          <Route path="/internal/dashboard" element={<ProtectedInternalRoute><InternalDashboard /></ProtectedInternalRoute>} />
          <Route path="/internal/admin-support" element={<ProtectedInternalRoute><SupportAccess /></ProtectedInternalRoute>} />
          <Route path="/internal/support-access" element={<ProtectedInternalRoute><SupportAccess /></ProtectedInternalRoute>} />
          <Route
            path="/reset-password-email"
            element={<ResetPasswordEmail />}
          />
          <Route
            path="/email-verification"
            element={<EmailVerificationResult />}
          />
          <Route path="*" element={<Navigate to="landing-page" />} />
        </>
        // renderRoutesForRole(userRole)
      )}
    </Routes>
  );
}

injectStore(store);

const App = () => {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
};

export default App;
